import { baseApi } from "@/services/api";
import type {
  ActivityHistoryQueryParams,
  ActivityHistoryResponse,
  GovernanceChangeAuditRequest,
  PatchEmployeeProfileRequest,
} from "./activityHistoryTypes";
import type { ApiEmployeeDetail } from "@/features/employees/employeeTypes";

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
}

function unwrap<T>(raw: ApiEnvelope<T>, fallbackMessage: string): T {
  if (!raw || raw.success !== true || !raw.data) {
    throw new Error(raw?.message || fallbackMessage);
  }
  return raw.data;
}

function buildActivityHistoryQuery(params: ActivityHistoryQueryParams): string {
  const search = new URLSearchParams();
  if (params.search) search.set("search", params.search);
  if (params.entityType && params.entityType !== "all") {
    search.set("entityType", params.entityType);
  } else if (params.userType && params.userType !== "all") {
    search.set("userType", params.userType);
  }
  if (params.activityType && params.activityType !== "all") {
    search.set("activityType", params.activityType);
  }
  if (params.employeeId) search.set("employeeId", params.employeeId);
  if (params.actorSub) search.set("actorSub", params.actorSub);
  if (params.entityRef) search.set("entityRef", params.entityRef);
  if (params.entityId) search.set("entityId", params.entityId);
  if (params.eventType) search.set("eventType", params.eventType);
  if (params.startDate) search.set("startDate", params.startDate);
  if (params.endDate) search.set("endDate", params.endDate);
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export const activityHistoryApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getActivityHistory: builder.query<ActivityHistoryResponse, ActivityHistoryQueryParams>({
      query: (params) => ({
        url: `/sa/activity-history${buildActivityHistoryQuery(params)}`,
        method: "GET",
      }),
      transformResponse: (raw: ApiEnvelope<ActivityHistoryResponse>) =>
        unwrap(raw, "Failed to load activity history"),
      keepUnusedDataFor: 0,
    }),
    patchEmployeeProfile: builder.mutation<
      { employee: ApiEmployeeDetail },
      { employeeId: string; body: PatchEmployeeProfileRequest }
    >({
      query: ({ employeeId, body }) => ({
        url: `/sa/employees/${encodeURIComponent(employeeId)}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<{ employee: ApiEmployeeDetail }>) =>
        unwrap(raw, "Failed to update employee profile"),
      invalidatesTags: (_result, _error, { employeeId }) => [
        { type: "Employees", id: employeeId },
        { type: "Employees", id: `${employeeId}-audit` },
        "Employees",
      ],
    }),
    recordEmployeePermissionsAudit: builder.mutation<
      { recorded: boolean },
      { employeeId: string; body: GovernanceChangeAuditRequest }
    >({
      query: ({ employeeId, body }) => ({
        url: `/sa/employees/${encodeURIComponent(employeeId)}/permissions`,
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<{ recorded: boolean }>) =>
        unwrap(raw, "Failed to record permissions audit"),
      invalidatesTags: ["Employees"],
    }),
    recordEmployeeAccessAreasAudit: builder.mutation<
      { recorded: boolean },
      { employeeId: string; body: GovernanceChangeAuditRequest }
    >({
      query: ({ employeeId, body }) => ({
        url: `/sa/employees/${encodeURIComponent(employeeId)}/access-areas`,
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<{ recorded: boolean }>) =>
        unwrap(raw, "Failed to record access areas audit"),
      invalidatesTags: ["Employees"],
    }),
  }),
});

export const {
  useGetActivityHistoryQuery,
  usePatchEmployeeProfileMutation,
  useRecordEmployeePermissionsAuditMutation,
  useRecordEmployeeAccessAreasAuditMutation,
} = activityHistoryApi;
